<template>
  <component :is="to ? RouterLink : 'a'" :to="to" :href="href" :target="href ? '_blank' : undefined"
    class="navigation-card" :style="cardStyle">
    <v-hover v-slot="{ isHovering, props }">
      <v-card
        v-bind="props"
        class="navigation-card__surface pa-6 text-center d-flex flex-column align-start justify-space-between"
        :class="{ 'is-hovering': isHovering }"
        height="190"
        width="100%"
      >
        <div class="navigation-card__icon">
          <v-icon size="34">{{ icon }}</v-icon>
        </div>

        <div class="navigation-card__title">
          {{ title }}
        </div>

        <v-icon class="navigation-card__arrow" size="22">mdi-arrow-right</v-icon>
      </v-card>
    </v-hover>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

const props = withDefaults(defineProps<{
  icon: string
  title: string
  to?: string // Link interno (rota Vue)
  href?: string // Link externo (URL completa)
  color?: string
}>(), {
  color: 'primary'
})

const toneMap: Record<string, string> = {
  primary: '#0b4f6c',
  teal: '#13768a',
  red: '#9f2d2d',
  green: '#1d7f5f',
  'light-green': '#1d7f5f',
  indigo: '#2f4d8f',
  'blue-grey': '#48677a',
}

const cardStyle = computed(() => ({
  '--card-color': toneMap[props.color] || toneMap.primary,
}))
</script>

<style scoped>
.navigation-card {
  text-decoration: none;
  width: 100%;
  max-width: 340px;
  color: inherit;
  transition: transform 0.25s ease;
}

.navigation-card:hover {
  transform: translateY(-6px);
}

.navigation-card__surface {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(8, 47, 73, 0.12);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.96), rgba(248, 251, 253, 0.9)),
    #fff !important;
  box-shadow: var(--funarpen-shadow-soft) !important;
  transition:
    border-color 0.25s ease,
    box-shadow 0.25s ease,
    background 0.25s ease;
}

.navigation-card__surface::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at top right, color-mix(in srgb, var(--card-color) 22%, transparent), transparent 48%),
    linear-gradient(90deg, var(--card-color), var(--funarpen-gold));
  opacity: 0.1;
  pointer-events: none;
}

.navigation-card__surface::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 5px;
  height: 100%;
  background: linear-gradient(180deg, var(--card-color), var(--funarpen-gold));
}

.navigation-card__surface.is-hovering {
  border-color: color-mix(in srgb, var(--card-color) 32%, transparent);
  box-shadow: var(--funarpen-shadow) !important;
}

.navigation-card__icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: var(--funarpen-radius);
  color: var(--card-color);
  background: color-mix(in srgb, var(--card-color) 10%, white);
  border: 1px solid color-mix(in srgb, var(--card-color) 18%, transparent);
}

.navigation-card__title {
  position: relative;
  max-width: 96%;
  color: var(--funarpen-ink);
  font-size: 1.06rem;
  font-weight: 900;
  line-height: 1.25;
  text-align: left;
  text-transform: none;
}

.navigation-card__arrow {
  position: absolute;
  right: 18px;
  bottom: 18px;
  color: var(--card-color);
  opacity: 0.72;
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.navigation-card:hover .navigation-card__arrow {
  opacity: 1;
  transform: translateX(4px);
}

/* Remove hover no mobile para evitar "travamento" */
@media (hover: none) and (pointer: coarse) {
  .navigation-card:hover {
    transform: none;
  }

  .navigation-card:focus-visible {
    transform: translateY(-4px);
  }
}
</style>
